/**
 * @file TermsGate.tsx
 * @brief One-time "agree to our Terms and Privacy Policy" screen on web and
 *        desktop for an account that has not accepted the current version
 *        (GDPR, 06-10-2026).
 *
 * @details
 * Mirrors the phone's TermsAcceptanceHost. Shown over every page while
 * `/auth/me` says `needsTermsAcceptance`; agreeing posts the version the
 * server says is current, signing out is the only other way out.
 *
 * @references
 * - BACKEND/src/features/auth/routes.js (GET /auth/terms, POST /auth/accept-terms)
 * - FRONTEND/src/screens/Auth/components/TermsAcceptanceHost.tsx
 *
 * @author DES
 * @date 06-10-2026
 */

import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { ExternalLink, LoaderCircle, ShieldCheck } from "lucide-react";
import { Button } from "@/components/ui/Button";
import { ApiClientError, apiGet, apiPost } from "@/shared/api/client";
import { authEndpoints } from "@/shared/api/endpoints";
import { useAuth } from "@/shared/auth/auth-context";
import { FALLBACKS } from "@/shared/publicConfig/publicConfig";
import { usePublicConfig } from "@/shared/publicConfig/usePublicConfig";

type TermsInfo = { version: string; termsUrl: string; privacyUrl: string };

export function TermsGate() {
  const { user, refreshUser, logout } = useAuth();
  const needs = user?.needsTermsAcceptance === true;
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const info = useQuery({
    queryKey: ["auth", "terms-info"],
    queryFn: () => apiGet<TermsInfo>(authEndpoints.termsInfo),
    enabled: needs,
    staleTime: 60 * 60 * 1000,
  });

  // The server's terms version names its own links; without them, the
  // Links & company info ones (B23), else the old links.
  const links = usePublicConfig();
  if (!needs) return null;

  const termsUrl = info.data?.termsUrl ?? links.value("TERMS_URL", FALLBACKS.TERMS_URL);
  const privacyUrl = info.data?.privacyUrl ?? links.value("PRIVACY_URL", FALLBACKS.PRIVACY_URL);

  const agree = async () => {
    if (!info.data?.version) {
      setError("Couldn't load the terms. Check your connection and try again.");
      return;
    }
    setBusy(true);
    setError(null);
    try {
      await apiPost(authEndpoints.acceptTerms, { version: info.data.version });
      await refreshUser();
    } catch (err) {
      setError(err instanceof ApiClientError ? err.message : "Couldn't save your answer. Please try again.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-labelledby="terms-gate-title"
      className="fixed inset-0 z-[100] flex items-center justify-center overflow-y-auto bg-background px-4 py-10"
    >
      <div className="flex w-full max-w-md flex-col items-center gap-5 text-center">
        <ShieldCheck className="h-16 w-16 text-brand" strokeWidth={1.25} />
        <h1 id="terms-gate-title" className="text-2xl font-semibold tracking-tight [text-wrap:balance]">
          Our terms and privacy policy
        </h1>
        <p className="text-sm text-muted-foreground">
          They say what Verblizr does with your recordings, transcripts and account details, and the
          rights you have over them. You can download your data or delete your account any time
          from Profile › ⋯. Please agree to keep using Verblizr.
        </p>
        <div className="flex flex-col items-center gap-2 text-sm font-medium">
          <a href={termsUrl} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1.5 text-brand hover:underline">
            Read the Terms of Service <ExternalLink className="h-3.5 w-3.5" />
          </a>
          <a href={privacyUrl} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1.5 text-brand hover:underline">
            Read the Privacy Policy <ExternalLink className="h-3.5 w-3.5" />
          </a>
        </div>
        {error ? <p className="text-sm text-destructive">{error}</p> : null}
        <div className="flex w-full flex-col gap-2 pt-2">
          <Button
            type="button"
            onClick={agree}
            disabled={busy || info.isLoading}
            className="h-12 w-full gap-2 bg-brand text-[15px] font-semibold text-brand-foreground hover:bg-brand/90"
          >
            {busy ? <LoaderCircle className="h-4 w-4 animate-spin" /> : null}
            Agree and continue
          </Button>
          <Button type="button" variant="ghost" onClick={() => void logout()} disabled={busy}>
            Sign out
          </Button>
        </div>
      </div>
    </div>
  );
}
